// src/components/Hero/Hero.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo?.name || "Your Studio";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    website: "",
    budget: "",
    services: { web: false, marketing: false },
    requirements: "",
  });

  const [status, setStatus] = useState({ loading: false, ok: "", err: "" });

  const headline = useMemo(
    () => ({
      top: "Growth-focused digital marketing",
      main: "Scale your brand with campaigns that convert.",
      sub:
        `${brandName} helps businesses worldwide with strategy, ads, landing pages, SEO, and retention systems—built to generate consistent leads and revenue.`,
    }),
    [brandName]
  );

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    setStatus({ loading: false, ok: "", err: "" });

    if (name === "web" || name === "marketing") {
      setForm((p) => ({
        ...p,
        services: { ...p.services, [name]: checked },
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return setStatus({ loading: false, ok: "", err: "Please enter your name." });
    if (!form.phone.trim()) return setStatus({ loading: false, ok: "", err: "Please enter your phone number." });

    try {
      setStatus({ loading: true, ok: "", err: "" });

      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        budget: form.budget.trim(),
        services: Object.keys(form.services).filter((k) => form.services[k]),
        requirements: form.requirements.trim(),
        source: "hero_lead_form",
      };

      if (typeof onSubmitLead === "function") {
        await onSubmitLead(payload);
      }

      setStatus({ loading: false, ok: "Thanks! We’ll reach out shortly.", err: "" });
      setForm({
        name: "",
        phone: "",
        website: "",
        budget: "",
        services: { web: false, marketing: false },
        requirements: "",
      });
    } catch (err) {
      setStatus({ loading: false, ok: "", err: "Something went wrong. Please try again." });
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
  };

  return (
    <section className="hero5" aria-label={`${brandName} hero`}>
      <div className="hero5-bg" aria-hidden="true" />
      <div className="hero5-shell">
        <motion.div className="hero5-grid" initial="hidden" animate="show" variants={container}>
          {/* LEFT */}
          <motion.div className="hero5-left" variants={fadeUp}>
            <div className="hero5-badge">
              <span className="hero5-badge-dot" aria-hidden="true" />
              <span>Worldwide • Strategy • Ads • SEO • Funnels</span>
            </div>

            <h1 className="hero5-title">
              <span className="hero5-title-top">{headline.top}</span>
              <span className="hero5-title-main">{headline.main}</span>
            </h1>

            <p className="hero5-sub">{headline.sub}</p>

            <div className="hero5-points" aria-label="Key benefits">
              <div className="hero5-point">
                <span className="hero5-point-k">Full-funnel</span>
                <span className="hero5-point-v">Ads → Landing → CRM → Retention</span>
              </div>
              <div className="hero5-point">
                <span className="hero5-point-k">Tracking</span>
                <span className="hero5-point-v">Events, pixels, dashboards & reporting</span>
              </div>
              <div className="hero5-point">
                <span className="hero5-point-k">Execution</span>
                <span className="hero5-point-v">Fast iterations, weekly optimization</span>
              </div>
            </div>

            <div className="hero5-trust">
              <div className="hero5-pill">Transparent reporting</div>
              <div className="hero5-pill">No long-term lock-in</div>
              <div className="hero5-pill">Global clients</div>
            </div>
          </motion.div>

          {/* RIGHT FORM */}
          <motion.aside className="hero5-card" variants={fadeUp} aria-label="Lead form">
            <h2 className="hero5-form-title">Let’s Work Together</h2>

            <form className="hero5-form" onSubmit={handleSubmit}>
              <label className="hero5-field">
                <span className="sr-only">Name</span>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Name"
                  autoComplete="name"
                />
              </label>

              <label className="hero5-field">
                <span className="sr-only">Phone</span>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Phone No."
                  autoComplete="tel"
                />
              </label>

              <label className="hero5-field">
                <span className="sr-only">Business Name or Website</span>
                <input
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  placeholder="Business Name or Website"
                  autoComplete="url"
                />
              </label>

              <label className="hero5-field">
                <span className="sr-only">Budget</span>
                <input
                  name="budget"
                  value={form.budget}
                  onChange={onChange}
                  placeholder="Budget, if any"
                />
              </label>

              <div className="hero5-services">
                <div className="hero5-services-label">Services</div>
                <label className="hero5-check">
                  <input
                    type="checkbox"
                    name="web"
                    checked={form.services.web}
                    onChange={onChange}
                  />
                  <span>Web Development</span>
                </label>
                <label className="hero5-check">
                  <input
                    type="checkbox"
                    name="marketing"
                    checked={form.services.marketing}
                    onChange={onChange}
                  />
                  <span>Digital Marketing</span>
                </label>
              </div>

              <label className="hero5-field hero5-field--area">
                <span className="sr-only">Requirements</span>
                <textarea
                  name="requirements"
                  value={form.requirements}
                  onChange={onChange}
                  placeholder="Requirements, if any"
                  rows={4}
                />
              </label>

              {status.err ? <div className="hero5-msg hero5-msg--err">{status.err}</div> : null}
              {status.ok ? <div className="hero5-msg hero5-msg--ok">{status.ok}</div> : null}

              <button className="hero5-submit" type="submit" disabled={status.loading}>
                {status.loading ? "SENDING..." : "SEND"}
              </button>

              <p className="hero5-note">
                By submitting, you agree to be contacted about your request.
              </p>
            </form>
          </motion.aside>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
